import { useState, useEffect, useRef } from "react";

const WAITLIST_KEY = "lova_waitlist";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, up = true }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? "translateY(0)" : up ? "translateY(32px)" : "translateY(0)", transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const TRANSLATIONS = {
  it: {
    nav: { features: "Features", pricing: "Prezzi", faq: "FAQ", cta: "Unisciti alla Waitlist →" },
    hero: {
      pill: "persone in lista d'attesa",
      title1: "La tua relazione,",
      title2: "finalmente illuminata.",
      subtitle: "LOVA è il diario intelligente che trasforma i tuoi stati d'animo in consapevolezza reale. Ogni giorno, un check-in. Ogni settimana, una rivelazione.",
      placeholder: "la-tua@email.com",
      btn: "Unisciti →",
      successTitle: "Sei nella lista!",
      successDesc: "Ti avvisiamo non appena LOVA apre. Grazie per crederci.",
      disclaimer: "Nessuno spam. Cancellati in un clic. I tuoi dati non vengono mai venduti.",
    },
    howItWorks: {
      title1: "Come funziona,",
      title2: "in pratica.",
      subtitle: "Due minuti al giorno. Una trasformazione nel tempo.",
      steps: [
        { n: "01", icon: "😊", title: "Fai il check-in", desc: "Selezioni il tuo mood, rispondi a 1-3 domande. In 10 secondi oppure in 5 minuti, come preferisci. Non c'è risposta giusta." },
        { n: "02", icon: "🌦️", title: "Vedi il tuo meteo", desc: "LOVA legge i tuoi ultimi 7 giorni e ti mostra un'immagine immediata: ☀️ sereno, ⛅ nuvoloso, 🌧️ tempestoso, 🌈 arcobaleno." },
        { n: "03", icon: "💡", title: "Ricevi l'insight", desc: "L'AI analizza i tuoi pattern reali e ti dice cose che non ti sei ancora detto. 'Ogni lunedì sei più ansioso. Ogni volta che scrivi di lei, il tuo mood migliora.'" },
        { n: "04", icon: "🌱", title: "Cresci davvero", desc: "Nel tempo, il quadro emerge. Le relazioni migliorano quando capiamo noi stessi. LOVA non ti cambia — ti aiuta a vederti." },
      ],
    },
    features: {
      title1: "Tutto quello che",
      title2: "ti serve.",
      subtitle: "Ogni feature è pensata per una cosa: aiutarti a capire.",
      items: [
        { icon: "🌦️", title: "Relationship Weather", desc: "Ogni giorno LOVA legge il tuo stato emotivo e ti mostra il meteo della tua relazione — da ☀️ Sereno a 🌧️ Tempestoso. Semplice. Immediato. Rivelatore." },
        { icon: "💡", title: "Insights AI Personali", desc: "Non consigli generici. L'intelligenza artificiale analizza i tuoi pattern reali e ti restituisce osservazioni su misura: trigger, tendenze, momenti di crescita." },
        { icon: "💑", title: "Modalità Coppia", desc: "Entrambi fate check-in. L'AI confronta i vostri stati d'animo e genera un insight condiviso. 'Oggi avete entrambi sentito distanza — ecco cosa potrebbe aiutare.'" },
        { icon: "🌱", title: "Healing Journey", desc: "Dopo una rottura, un percorso guidato in 5 fasi: dall'accettazione alla chiusura. Domande diverse ogni giorno. Un coach empatico sempre disponibile." },
        { icon: "🔐", title: "Vault Privato", desc: "I pensieri più vulnerabili meritano protezione totale. Il Vault è cifrato lato client: nemmeno noi possiamo leggerlo. Solo tu, il tuo PIN, i tuoi segreti." },
        { icon: "🏆", title: "Streak & Achievement", desc: "La costanza trasforma la riflessione in abitudine. Ogni giorno conta. Ogni streak sblocca qualcosa. Ogni traguardo è tuo, davvero." },
      ],
    },
    testimonials: {
      title1: "Chi l'ha già usata",
      title2: "dice così.",
      items: [
        { text: "Dopo 3 settimane di check-in ho capito perché litigavamo sempre il lunedì. Sembrava magia, era solo consapevolezza.", name: "Giulia R.", role: "Beta tester · Milano" },
        { text: "Il Vault mi ha salvata. Avevo bisogno di uno spazio dove scrivere cose che non avrei mai detto a nessuno. Ora le dico a me stessa.", name: "Sofia M.", role: "Beta tester · Roma" },
        { text: "Usavo già un diario cartaceo. LOVA è quello, ma con un amico che legge tutto e ti fa notare le cose che non vedi tu.", name: "Marco T.", role: "Beta tester · Torino" },
      ],
    },
    pricing: {
      title1: "Prezzi",
      title2: "onesti.",
      subtitle: "Inizia gratis. Passa a Premium quando senti il valore. Cancella in un clic.",
      mostChosen: "PIÙ SCELTO",
      footer: "Cancella in qualsiasi momento · Garanzia rimborso 30 giorni · I dati tuoi, sempre",
      plans: [
        { name: "Free", price: "€0", period: "", desc: "Per iniziare il viaggio", features: ["3 check-in a settimana", "Weather giornaliero", "Streak tracker", "1 insight mensile"], cta: "Inizia gratis", ghost: true },
        { name: "Premium", price: "€9.99", period: "/mese", desc: "Per chi vuole davvero capire", pop: true, features: ["Check-in illimitati", "Insights AI completi", "Modalità Coppia", "Year in Review", "Export PDF", "Tutti i badge"], cta: "Prova 7 giorni gratis" },
        { name: "Pro", price: "€19.99", period: "/mese", desc: "Per chi vuole trasformarsi", features: ["Tutto di Premium +", "Voice note AI", "Healing Journey", "Crisis Mode", "Coaching 15min/mese", "Priority support"], cta: "Scegli Pro" },
        { name: "Enterprise", price: "€99", period: "/mese", desc: "Per terapeuti e studi", features: ["Dashboard 20+ pazienti", "Insights clinici personalizzati", "White-label option", "GDPR & HIPAA compliant", "Supporto dedicato", "Onboarding guidato"], cta: "Contattaci" },
      ],
    },
    privacy: {
      title1: "La tua privacy è",
      title2: "sacra.",
      desc: "LOVA tratta i dati più sensibili che esistano — i tuoi stati d'animo, le tue relazioni, i tuoi pensieri più privati. Ci prendiamo questa responsabilità sul serio. Tutto è cifrato. Il Vault è end-to-end: nemmeno noi possiamo leggerlo. I tuoi dati non vengono mai venduti. Mai.",
      badges: [
        { icon: "🔐", label: "Cifratura E2E", sub: "Vault inaccessibile" },
        { icon: "🇪🇺", label: "GDPR Compliant", sub: "Server in EU" },
        { icon: "🚫", label: "No Data Sale", sub: "Mai, per nessun motivo" },
      ],
    },
    faq: {
      title1: "Domande",
      title2: "frequenti.",
      items: [
        { q: "LOVA sostituisce la terapia?", a: "No, e non vuole farlo. LOVA è uno strumento di consapevolezza personale. Se stai attraversando un momento difficile, ti incoraggiamo a rivolgerti a un professionista. LOVA può affiancare un percorso terapeutico, non sostituirlo." },
        { q: "I miei dati sono al sicuro?", a: "Sì. I check-in sono cifrati in transito e a riposo. Il Vault usa cifratura end-to-end lato client: nemmeno il nostro server può leggere i tuoi pensieri privati. Non vendiamo mai i dati a terzi, mai." },
        { q: "Funziona anche senza partner?", a: "Assolutamente. La modalità Solo è pensata per chi vuole capire se stesso, le proprie dinamiche relazionali, i propri pattern emotivi — indipendentemente dall'avere un partner." },
        { q: "Posso cancellare tutto?", a: "Sì, in qualsiasi momento. Un clic nelle impostazioni elimina account e tutti i dati in modo permanente e irreversibile entro 30 giorni, inclusi i backup." },
        { q: "Quanto tempo richiede al giorno?", a: "Il check-in rapido richiede 10 secondi — selezioni un emoji e finisci. Il check-in completo, con le domande aperte, richiede 2-5 minuti. LOVA si adatta al tempo che hai." },
      ],
    },
    finalCta: {
      title1: "Inizia il viaggio.",
      title2: "Capisciti davvero.",
      subtitle: "Entra nella lista d'attesa. Ti avvisiamo non appena LOVA apre le porte.",
      successTitle: "Sei dentro!",
      successDesc: "Ti scriviamo presto.",
      footer: "persone ci stanno già aspettando. Nessuno spam, mai.",
    },
    footer: {
      copy: "© 2024 LOVA. Tutti i diritti riservati. · La tua relazione, illuminata.",
      links: ["Privacy Policy", "Terms of Service", "Contatti"],
    },
  },
  en: {
    nav: { features: "Features", pricing: "Pricing", faq: "FAQ", cta: "Join the Waitlist →" },
    hero: {
      pill: "people on the waitlist",
      title1: "Your relationship,",
      title2: "finally illuminated.",
      subtitle: "LOVA is the smart journal that transforms your emotions into real self-awareness. Every day, a check-in. Every week, a revelation.",
      placeholder: "your@email.com",
      btn: "Join →",
      successTitle: "You're on the list!",
      successDesc: "We'll let you know as soon as LOVA opens. Thank you for believing in us.",
      disclaimer: "No spam. Unsubscribe in one click. Your data is never sold.",
    },
    howItWorks: {
      title1: "How it works,",
      title2: "in practice.",
      subtitle: "Two minutes a day. A transformation over time.",
      steps: [
        { n: "01", icon: "😊", title: "Do your check-in", desc: "Pick your mood, answer 1-3 questions. In 10 seconds or 5 minutes, your choice. There's no wrong answer." },
        { n: "02", icon: "🌦️", title: "See your weather", desc: "LOVA reads your last 7 days and shows you an instant picture: ☀️ sunny, ⛅ cloudy, 🌧️ stormy, 🌈 rainbow." },
        { n: "03", icon: "💡", title: "Get your insight", desc: "The AI analyzes your real patterns and tells you things you haven't told yourself yet. 'Every Monday you're more anxious. Every time you write about her, your mood improves.'" },
        { n: "04", icon: "🌱", title: "Truly grow", desc: "Over time, the picture emerges. Relationships improve when we understand ourselves. LOVA doesn't change you — it helps you see yourself." },
      ],
    },
    features: {
      title1: "Everything you",
      title2: "need.",
      subtitle: "Every feature is designed for one thing: helping you understand.",
      items: [
        { icon: "🌦️", title: "Relationship Weather", desc: "Every day LOVA reads your emotional state and shows you your relationship weather — from ☀️ Sunny to 🌧️ Stormy. Simple. Immediate. Revealing." },
        { icon: "💡", title: "Personal AI Insights", desc: "Not generic advice. The AI analyzes your real patterns and gives you tailored observations: triggers, trends, growth moments." },
        { icon: "💑", title: "Couple Mode", desc: "Both of you check in. The AI compares your moods and generates a shared insight. 'Today you both felt distance — here's what might help.'" },
        { icon: "🌱", title: "Healing Journey", desc: "After a breakup, a guided path in 5 phases: from acceptance to closure. Different questions every day. An empathetic coach always available." },
        { icon: "🔐", title: "Private Vault", desc: "Your most vulnerable thoughts deserve total protection. The Vault is client-side encrypted: even we can't read it. Just you, your PIN, your secrets." },
        { icon: "🏆", title: "Streak & Achievement", desc: "Consistency turns reflection into habit. Every day counts. Every streak unlocks something. Every milestone is truly yours." },
      ],
    },
    testimonials: {
      title1: "Those who tried it",
      title2: "say this.",
      items: [
        { text: "After 3 weeks of check-ins I understood why we always fought on Mondays. It seemed like magic, it was just awareness.", name: "Giulia R.", role: "Beta tester · Milan" },
        { text: "The Vault saved me. I needed a space to write things I would never say to anyone. Now I say them to myself.", name: "Sofia M.", role: "Beta tester · Rome" },
        { text: "I was already using a paper diary. LOVA is that, but with a friend who reads everything and points out the things you don't see.", name: "Marco T.", role: "Beta tester · Turin" },
      ],
    },
    pricing: {
      title1: "Honest",
      title2: "pricing.",
      subtitle: "Start free. Upgrade to Premium when you feel the value. Cancel in one click.",
      mostChosen: "MOST POPULAR",
      footer: "Cancel anytime · 30-day money-back guarantee · Your data, always yours",
      plans: [
        { name: "Free", price: "€0", period: "", desc: "To start the journey", features: ["3 check-ins per week", "Daily weather", "Streak tracker", "1 monthly insight"], cta: "Start free", ghost: true },
        { name: "Premium", price: "€9.99", period: "/month", desc: "For those who truly want to understand", pop: true, features: ["Unlimited check-ins", "Full AI insights", "Couple Mode", "Year in Review", "PDF Export", "All badges"], cta: "Try 7 days free" },
        { name: "Pro", price: "€19.99", period: "/month", desc: "For those who want to transform", features: ["Everything in Premium +", "AI Voice notes", "Healing Journey", "Crisis Mode", "15min/month coaching", "Priority support"], cta: "Choose Pro" },
        { name: "Enterprise", price: "€99", period: "/month", desc: "For therapists & practices", features: ["20+ patient dashboard", "Custom clinical insights", "White-label option", "GDPR & HIPAA compliant", "Dedicated support", "Guided onboarding"], cta: "Contact us" },
      ],
    },
    privacy: {
      title1: "Your privacy is",
      title2: "sacred.",
      desc: "LOVA handles the most sensitive data there is — your moods, your relationships, your most private thoughts. We take this responsibility seriously. Everything is encrypted. The Vault is end-to-end: even we can't read it. Your data is never sold. Ever.",
      badges: [
        { icon: "🔐", label: "E2E Encryption", sub: "Inaccessible Vault" },
        { icon: "🇪🇺", label: "GDPR Compliant", sub: "EU Servers" },
        { icon: "🚫", label: "No Data Sale", sub: "Never, for any reason" },
      ],
    },
    faq: {
      title1: "Frequently",
      title2: "asked questions.",
      items: [
        { q: "Does LOVA replace therapy?", a: "No, and it doesn't want to. LOVA is a personal awareness tool. If you're going through a difficult time, we encourage you to reach out to a professional. LOVA can complement a therapeutic path, not replace it." },
        { q: "Is my data safe?", a: "Yes. Check-ins are encrypted in transit and at rest. The Vault uses client-side end-to-end encryption: even our server can't read your private thoughts. We never sell data to third parties, ever." },
        { q: "Does it work without a partner?", a: "Absolutely. Solo mode is designed for those who want to understand themselves, their relational dynamics, their emotional patterns — regardless of having a partner." },
        { q: "Can I delete everything?", a: "Yes, at any time. One click in settings permanently and irreversibly deletes your account and all data within 30 days, including backups." },
        { q: "How much time does it take per day?", a: "The quick check-in takes 10 seconds — you select an emoji and you're done. The full check-in, with open questions, takes 2-5 minutes. LOVA adapts to the time you have." },
      ],
    },
    finalCta: {
      title1: "Start the journey.",
      title2: "Truly understand yourself.",
      subtitle: "Join the waitlist. We'll let you know as soon as LOVA opens its doors.",
      successTitle: "You're in!",
      successDesc: "We'll write to you soon.",
      footer: "people are already waiting. No spam, ever.",
    },
    footer: {
      copy: "© 2024 LOVA. All rights reserved. · Your relationship, illuminated.",
      links: ["Privacy Policy", "Terms of Service", "Contact"],
    },
  },
  pt: {
    nav: { features: "Recursos", pricing: "Preços", faq: "FAQ", cta: "Entre na Waitlist →" },
    hero: {
      pill: "pessoas na lista de espera",
      title1: "Seu relacionamento,",
      title2: "finalmente iluminado.",
      subtitle: "LOVA é o diário inteligente que transforma suas emoções em consciência real. Todo dia, um check-in. Toda semana, uma revelação.",
      placeholder: "seu@email.com",
      btn: "Entrar →",
      successTitle: "Você está na lista!",
      successDesc: "Avisaremos assim que o LOVA abrir. Obrigado por acreditar em nós.",
      disclaimer: "Sem spam. Cancele em um clique. Seus dados nunca são vendidos.",
    },
    howItWorks: {
      title1: "Como funciona,",
      title2: "na prática.",
      subtitle: "Dois minutos por dia. Uma transformação ao longo do tempo.",
      steps: [
        { n: "01", icon: "😊", title: "Faça seu check-in", desc: "Escolha seu humor, responda 1-3 perguntas. Em 10 segundos ou 5 minutos, como preferir. Não existe resposta errada." },
        { n: "02", icon: "🌦️", title: "Veja seu clima", desc: "LOVA lê seus últimos 7 dias e mostra uma imagem imediata: ☀️ ensolarado, ⛅ nublado, 🌧️ tempestuoso, 🌈 arco-íris." },
        { n: "03", icon: "💡", title: "Receba o insight", desc: "A IA analisa seus padrões reais e diz coisas que você ainda não disse a si mesmo. 'Toda segunda você fica mais ansioso. Toda vez que escreve sobre ela, seu humor melhora.'" },
        { n: "04", icon: "🌱", title: "Cresça de verdade", desc: "Com o tempo, o quadro surge. Os relacionamentos melhoram quando nos entendemos. LOVA não te muda — te ajuda a se ver." },
      ],
    },
    features: {
      title1: "Tudo o que você",
      title2: "precisa.",
      subtitle: "Cada recurso é pensado para uma coisa: te ajudar a entender.",
      items: [
        { icon: "🌦️", title: "Relationship Weather", desc: "Todo dia o LOVA lê seu estado emocional e mostra o clima do seu relacionamento — de ☀️ Ensolarado a 🌧️ Tempestuoso. Simples. Imediato. Revelador." },
        { icon: "💡", title: "Insights AI Pessoais", desc: "Não são conselhos genéricos. A IA analisa seus padrões reais e entrega observações sob medida: gatilhos, tendências, momentos de crescimento." },
        { icon: "💑", title: "Modo Casal", desc: "Ambos fazem check-in. A IA compara os humores e gera um insight compartilhado. 'Hoje ambos sentiram distância — veja o que pode ajudar.'" },
        { icon: "🌱", title: "Healing Journey", desc: "Após um término, um caminho guiado em 5 fases: da aceitação ao encerramento. Perguntas diferentes todo dia. Um coach empático sempre disponível." },
        { icon: "🔐", title: "Vault Privado", desc: "Seus pensamentos mais vulneráveis merecem proteção total. O Vault é criptografado no cliente: nem nós podemos ler. Só você, seu PIN, seus segredos." },
        { icon: "🏆", title: "Streak & Achievement", desc: "A constância transforma a reflexão em hábito. Cada dia conta. Cada streak desbloqueia algo. Cada conquista é verdadeiramente sua." },
      ],
    },
    testimonials: {
      title1: "Quem já usou",
      title2: "diz isso.",
      items: [
        { text: "Depois de 3 semanas de check-ins entendi por que sempre brigávamos às segundas. Parecia mágica, era apenas consciência.", name: "Giulia R.", role: "Beta tester · Milão" },
        { text: "O Vault me salvou. Eu precisava de um espaço para escrever coisas que nunca diria a ninguém. Agora digo a mim mesma.", name: "Sofia M.", role: "Beta tester · Roma" },
        { text: "Eu já usava um diário de papel. LOVA é isso, mas com um amigo que lê tudo e aponta as coisas que você não vê.", name: "Marco T.", role: "Beta tester · Turim" },
      ],
    },
    pricing: {
      title1: "Preços",
      title2: "honestos.",
      subtitle: "Comece grátis. Passe para Premium quando sentir o valor. Cancele em um clique.",
      mostChosen: "MAIS POPULAR",
      footer: "Cancele a qualquer momento · Garantia de reembolso de 30 dias · Seus dados, sempre seus",
      plans: [
        { name: "Free", price: "€0", period: "", desc: "Para começar a jornada", features: ["3 check-ins por semana", "Clima diário", "Streak tracker", "1 insight mensal"], cta: "Comece grátis", ghost: true },
        { name: "Premium", price: "€9.99", period: "/mês", desc: "Para quem quer realmente entender", pop: true, features: ["Check-ins ilimitados", "Insights AI completos", "Modo Casal", "Year in Review", "Export PDF", "Todos os badges"], cta: "Teste 7 dias grátis" },
        { name: "Pro", price: "€19.99", period: "/mês", desc: "Para quem quer se transformar", features: ["Tudo do Premium +", "Voice note AI", "Healing Journey", "Crisis Mode", "Coaching 15min/mês", "Suporte prioritário"], cta: "Escolha Pro" },
        { name: "Enterprise", price: "€99", period: "/mês", desc: "Para terapeutas e clínicas", features: ["Dashboard 20+ pacientes", "Insights clínicos personalizados", "Opção white-label", "GDPR & HIPAA compliant", "Suporte dedicado", "Onboarding guiado"], cta: "Fale conosco" },
      ],
    },
    privacy: {
      title1: "Sua privacidade é",
      title2: "sagrada.",
      desc: "LOVA lida com os dados mais sensíveis que existem — seus humores, seus relacionamentos, seus pensamentos mais privados. Levamos essa responsabilidade a sério. Tudo é criptografado. O Vault é end-to-end: nem nós podemos ler. Seus dados nunca são vendidos. Nunca.",
      badges: [
        { icon: "🔐", label: "Criptografia E2E", sub: "Vault inacessível" },
        { icon: "🇪🇺", label: "GDPR Compliant", sub: "Servidores na UE" },
        { icon: "🚫", label: "Sem Venda de Dados", sub: "Nunca, por nenhum motivo" },
      ],
    },
    faq: {
      title1: "Perguntas",
      title2: "frequentes.",
      items: [
        { q: "O LOVA substitui a terapia?", a: "Não, e não quer fazê-lo. LOVA é uma ferramenta de consciência pessoal. Se você está passando por um momento difícil, encorajamos você a procurar um profissional. LOVA pode complementar um caminho terapêutico, não substituí-lo." },
        { q: "Meus dados estão seguros?", a: "Sim. Os check-ins são criptografados em trânsito e em repouso. O Vault usa criptografia end-to-end no cliente: nem nosso servidor pode ler seus pensamentos privados. Nunca vendemos dados a terceiros, nunca." },
        { q: "Funciona sem parceiro?", a: "Absolutamente. O modo Solo é pensado para quem quer entender a si mesmo, suas dinâmicas relacionais, seus padrões emocionais — independentemente de ter um parceiro." },
        { q: "Posso apagar tudo?", a: "Sim, a qualquer momento. Um clique nas configurações elimina conta e todos os dados de forma permanente e irreversível em 30 dias, incluindo backups." },
        { q: "Quanto tempo leva por dia?", a: "O check-in rápido leva 10 segundos — você seleciona um emoji e pronto. O check-in completo, com perguntas abertas, leva 2-5 minutos. LOVA se adapta ao tempo que você tem." },
      ],
    },
    finalCta: {
      title1: "Comece a jornada.",
      title2: "Entenda-se de verdade.",
      subtitle: "Entre na lista de espera. Avisaremos assim que o LOVA abrir as portas.",
      successTitle: "Você está dentro!",
      successDesc: "Escrevemos em breve.",
      footer: "pessoas já estão esperando. Sem spam, nunca.",
    },
    footer: {
      copy: "© 2024 LOVA. Todos os direitos reservados. · Seu relacionamento, iluminado.",
      links: ["Política de Privacidade", "Termos de Serviço", "Contato"],
    },
  },
};

const LANG_FLAGS = { it: "🇮🇹", en: "🇬🇧", pt: "🇧🇷" };
const LANG_LABELS = { it: "IT", en: "EN", pt: "PT" };

export default function LOVALanding() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [count, setCount] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState("it");
  const [langOpen, setLangOpen] = useState(false);
  const heroRef = useRef(null);
  const langRef = useRef(null);

  const t = TRANSLATIONS[lang];

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(WAITLIST_KEY) || "[]");
    setCount(saved.length + 247);
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    const saved = JSON.parse(localStorage.getItem(WAITLIST_KEY) || "[]");
    if (!saved.includes(email)) {
      saved.push(email);
      localStorage.setItem(WAITLIST_KEY, JSON.stringify(saved));
      setCount(c => c + 1);
    }
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", background: "#0D0710", color: "#F0EAF4", margin: 0, padding: 0, overflowX: "hidden" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=DM+Sans:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0D0710; }
        ::selection { background: #FF6B9D44; color: #FF6B9D; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #1A0A14; }
        ::-webkit-scrollbar-thumb { background: #FF6B9D55; border-radius: 2px; }

        .serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        .sans { font-family: 'DM Sans', system-ui, sans-serif; }

        .nav-link {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 500;
          color: rgba(240,234,244,0.65);
          text-decoration: none; cursor: pointer;
          transition: color 0.25s;
          letter-spacing: 0.3px;
        }
        .nav-link:hover { color: #FF6B9D; }

        .btn-primary {
          background: linear-gradient(135deg, #FF6B9D, #C44569);
          color: white; border: none; border-radius: 50px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600; cursor: pointer;
          transition: all 0.3s;
          letter-spacing: 0.3px;
        }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(255,107,157,0.4); }
        .btn-primary:active { transform: scale(0.98); }

        .btn-ghost {
          background: transparent;
          border: 1.5px solid rgba(255,107,157,0.5);
          color: #FF6B9D; border-radius: 50px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600; cursor: pointer;
          transition: all 0.3s;
          letter-spacing: 0.3px;
        }
        .btn-ghost:hover { border-color: #FF6B9D; background: rgba(255,107,157,0.08); transform: translateY(-2px); }

        .feat-card {
          background: linear-gradient(135deg, rgba(255,107,157,0.06), rgba(255,107,157,0.02));
          border: 1px solid rgba(255,107,157,0.15);
          border-radius: 24px; padding: 32px 28px;
          transition: all 0.35s;
          position: relative; overflow: hidden;
        }
        .feat-card::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,107,157,0.08), transparent);
          opacity: 0; transition: opacity 0.35s;
        }
        .feat-card:hover { transform: translateY(-6px); border-color: rgba(255,107,157,0.35); box-shadow: 0 20px 50px rgba(255,107,157,0.1); }
        .feat-card:hover::before { opacity: 1; }

        .price-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 28px; padding: 36px 28px;
          transition: all 0.3s;
        }
        .price-card:hover { border-color: rgba(255,107,157,0.3); transform: translateY(-4px); }
        .price-card.pop {
          background: linear-gradient(160deg, rgba(255,107,157,0.12), rgba(196,69,105,0.06));
          border-color: rgba(255,107,157,0.4);
          box-shadow: 0 0 60px rgba(255,107,157,0.1);
        }

        .test-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 24px; padding: 32px 28px;
          transition: all 0.3s;
        }
        .test-card:hover { border-color: rgba(255,107,157,0.2); transform: translateY(-3px); }

        .faq-item {
          border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 24px 0; cursor: pointer;
          transition: all 0.2s;
        }
        .faq-item:hover { padding-left: 8px; }

        .email-input {
          background: rgba(255,255,255,0.06);
          border: 1.5px solid rgba(255,107,157,0.25);
          border-radius: 50px; color: #F0EAF4;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px; outline: none;
          transition: all 0.3s;
        }
        .email-input::placeholder { color: rgba(240,234,244,0.35); }
        .email-input:focus { border-color: #FF6B9D; background: rgba(255,107,157,0.08); box-shadow: 0 0 0 4px rgba(255,107,157,0.1); }

        @keyframes float { 0%,100%{transform:translateY(0) rotate(-1deg)} 50%{transform:translateY(-18px) rotate(1deg)} }
        @keyframes float2 { 0%,100%{transform:translateY(0) rotate(2deg)} 50%{transform:translateY(-12px) rotate(-1deg)} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.5} 100%{transform:scale(1.6);opacity:0} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

        .glow-text {
          background: linear-gradient(135deg, #FF6B9D, #FFC3A0, #FF6B9D);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }

        .grain {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          opacity: 0.025;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          background-repeat: repeat; background-size: 128px;
        }

        .lang-dropdown {
          position: absolute; top: 100%; right: 0; margin-top: 6px;
          background: rgba(13,7,16,0.96); backdrop-filter: blur(20px);
          border: 1px solid rgba(255,107,157,0.2); border-radius: 12px;
          overflow: hidden; min-width: 120px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.5);
        }
        .lang-option {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 16px; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500;
          color: rgba(240,234,244,0.65); transition: all 0.2s;
          border: none; background: transparent; width: 100%; text-align: left;
        }
        .lang-option:hover { background: rgba(255,107,157,0.1); color: #FF6B9D; }
        .lang-option.active { color: #FF6B9D; }

        @media (max-width: 768px) {
          .hero-title { font-size: clamp(42px, 10vw, 80px) !important; }
          .feat-grid { grid-template-columns: 1fr !important; }
          .price-grid { grid-template-columns: 1fr !important; }
          .test-grid { grid-template-columns: 1fr !important; }
          .hero-email { flex-direction: column !important; }
          .hero-email input { width: 100% !important; }
          .hero-email button { width: 100% !important; }
          .nav-links { display: none !important; }
        }
      `}</style>

      {/* Grain overlay */}
      <div className="grain" />

      {/* Ambient glows */}
      <div style={{ position: "fixed", top: -200, left: "50%", transform: "translateX(-50%)", width: 800, height: 800, background: "radial-gradient(circle, rgba(255,107,157,0.08) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      {/* NAV */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, transition: "all 0.4s", background: scrolled ? "rgba(13,7,16,0.92)" : "transparent", backdropFilter: scrolled ? "blur(20px)" : "none", borderBottom: scrolled ? "1px solid rgba(255,107,157,0.1)" : "1px solid transparent", padding: "0 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="serif" style={{ fontSize: 26, fontStyle: "italic", color: "#FF6B9D", letterSpacing: "-0.5px" }}>Lova</div>
          <div className="nav-links" style={{ display: "flex", gap: 32, alignItems: "center" }}>
            <span className="nav-link" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}>{t.nav.features}</span>
            <span className="nav-link" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>{t.nav.pricing}</span>
            <span className="nav-link" onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}>{t.nav.faq}</span>

            {/* Language Selector */}
            <div ref={langRef} style={{ position: "relative" }}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="sans"
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "rgba(255,107,157,0.08)", border: "1px solid rgba(255,107,157,0.2)",
                  borderRadius: 8, padding: "6px 12px", cursor: "pointer",
                  color: "rgba(240,234,244,0.7)", fontSize: 13, fontWeight: 500,
                  transition: "all 0.2s",
                }}
              >
                <span>{LANG_FLAGS[lang]}</span>
                <span>{LANG_LABELS[lang]}</span>
                <span style={{ fontSize: 10, marginLeft: 2, transition: "transform 0.2s", transform: langOpen ? "rotate(180deg)" : "rotate(0)" }}>▼</span>
              </button>
              {langOpen && (
                <div className="lang-dropdown">
                  {Object.keys(TRANSLATIONS).map((l) => (
                    <button
                      key={l}
                      className={`lang-option${lang === l ? " active" : ""}`}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                    >
                      <span>{LANG_FLAGS[l]}</span>
                      <span>{LANG_LABELS[l]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button className="btn-primary sans" style={{ padding: "10px 22px", fontSize: 13 }} onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}>
            {t.nav.cta}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "120px 24px 80px", textAlign: "center", position: "relative", zIndex: 1 }}>
        {/* Floating elements */}
        <div style={{ position: "absolute", top: "15%", left: "8%", fontSize: 48, animation: "float 6s ease-in-out infinite", opacity: 0.6 }}>💗</div>
        <div style={{ position: "absolute", top: "25%", right: "10%", fontSize: 36, animation: "float2 5s ease-in-out infinite", opacity: 0.5 }}>🌸</div>
        <div style={{ position: "absolute", bottom: "25%", left: "12%", fontSize: 30, animation: "float 7s ease-in-out infinite 1s", opacity: 0.4 }}>✨</div>
        <div style={{ position: "absolute", bottom: "20%", right: "8%", fontSize: 40, animation: "float2 6s ease-in-out infinite 0.5s", opacity: 0.45 }}>🌙</div>

        {/* Pill */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,107,157,0.1)", border: "1px solid rgba(255,107,157,0.25)", borderRadius: 50, padding: "8px 18px", marginBottom: 36, animation: "float 4s ease-in-out infinite" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF6B9D", position: "relative" }}>
            <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "1px solid #FF6B9D", animation: "pulse-ring 1.5s ease infinite" }} />
          </div>
          <span className="sans" style={{ fontSize: 12, color: "#FF6B9D", fontWeight: 600, letterSpacing: 0.5 }}>{count} {t.hero.pill}</span>
        </div>

        {/* Title */}
        <h1 className="serif hero-title" style={{ fontSize: "clamp(52px,8vw,96px)", fontWeight: 300, lineHeight: 1.05, letterSpacing: "-2px", marginBottom: 28, maxWidth: 800 }}>
          {t.hero.title1}<br />
          <span className="glow-text" style={{ fontStyle: "italic" }}>{t.hero.title2}</span>
        </h1>

        {/* Subtitle */}
        <p className="sans" style={{ fontSize: "clamp(16px,2vw,20px)", color: "rgba(240,234,244,0.6)", lineHeight: 1.7, maxWidth: 560, marginBottom: 52, fontWeight: 300 }}>
          {t.hero.subtitle}
        </p>

        {/* Waitlist form */}
        <div id="waitlist">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="hero-email" style={{ display: "flex", gap: 10, maxWidth: 500, width: "100%", justifyContent: "center" }}>
              <input
                className="email-input"
                type="email"
                placeholder={t.hero.placeholder}
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ flex: 1, padding: "15px 24px", minWidth: 200 }}
                required
              />
              <button type="submit" className="btn-primary" style={{ padding: "15px 28px", fontSize: 15, whiteSpace: "nowrap" }}>
                {t.hero.btn}
              </button>
            </form>
          ) : (
            <div style={{ background: "rgba(255,107,157,0.1)", border: "1px solid rgba(255,107,157,0.3)", borderRadius: 20, padding: "20px 36px", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💗</div>
              <div className="serif" style={{ fontSize: 22, fontStyle: "italic", color: "#FF6B9D", marginBottom: 6 }}>{t.hero.successTitle}</div>
              <div className="sans" style={{ fontSize: 14, color: "rgba(240,234,244,0.6)" }}>{t.hero.successDesc}</div>
            </div>
          )}
          <p className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.3)", marginTop: 14 }}>
            {t.hero.disclaimer}
          </p>
        </div>

      </section>

      {/* TICKER */}
      <div style={{ background: "linear-gradient(135deg, #FF6B9D22, #C4456911)", borderTop: "1px solid rgba(255,107,157,0.2)", borderBottom: "1px solid rgba(255,107,157,0.2)", padding: "14px 0", overflow: "hidden", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", animation: "ticker 20s linear infinite", whiteSpace: "nowrap" }}>
          {[...Array(2)].map((_, ii) => (
            <div key={ii} style={{ display: "flex", gap: 48, paddingRight: 48 }}>
              {["Relationship Weather ☀️", "AI Insights 💡", "Vault Privato 🔐", "Modalità Coppia 💑", "Healing Journey 🌱", "Check-in Rapido ⚡", "Year in Review 🌟", "Crisis Support 🆘"].map((txt, i) => (
                <span key={i} className="sans" style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,107,157,0.8)", letterSpacing: 0.3 }}>{txt}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* DEMO MOCKUP */}
      <section style={{ padding: "100px 24px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <h2 className="serif" style={{ fontSize: "clamp(36px,5vw,60px)", fontWeight: 300, letterSpacing: "-1px", marginBottom: 16 }}>
              {t.howItWorks.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.howItWorks.title2}</span>
            </h2>
            <p className="sans" style={{ fontSize: 17, color: "rgba(240,234,244,0.55)", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
              {t.howItWorks.subtitle}
            </p>
          </div>
        </FadeIn>

        {/* Steps */}
        {t.howItWorks.steps.map((step, i) => (
          <FadeIn key={i} delay={i * 0.12}>
            <div style={{ display: "flex", gap: 32, alignItems: "flex-start", marginBottom: 48, flexWrap: "wrap" }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,107,157,0.1)", border: "1px solid rgba(255,107,157,0.25)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{step.icon}</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="sans" style={{ fontSize: 11, color: "#FF6B9D", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>{step.n}</div>
                <div className="serif" style={{ fontSize: 26, fontWeight: 400, marginBottom: 10, letterSpacing: "-0.5px" }}>{step.title}</div>
                <div className="sans" style={{ fontSize: 16, color: "rgba(240,234,244,0.55)", lineHeight: 1.7 }}>{step.desc}</div>
              </div>
            </div>
          </FadeIn>
        ))}
      </section>

      {/* FEATURES */}
      <section id="features" style={{ padding: "80px 24px 100px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 className="serif" style={{ fontSize: "clamp(36px,5vw,60px)", fontWeight: 300, letterSpacing: "-1px", marginBottom: 16 }}>
              {t.features.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.features.title2}</span>
            </h2>
            <p className="sans" style={{ fontSize: 17, color: "rgba(240,234,244,0.55)", maxWidth: 400, margin: "0 auto" }}>
              {t.features.subtitle}
            </p>
          </div>
        </FadeIn>
        <div className="feat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
          {t.features.items.map((f, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="feat-card">
                <div style={{ fontSize: 36, marginBottom: 18 }}>{f.icon}</div>
                <div className="serif" style={{ fontSize: 22, fontWeight: 400, marginBottom: 12, letterSpacing: "-0.3px" }}>{f.title}</div>
                <div className="sans" style={{ fontSize: 14, color: "rgba(240,234,244,0.55)", lineHeight: 1.75 }}>{f.desc}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: "80px 24px", background: "rgba(255,107,157,0.03)", borderTop: "1px solid rgba(255,107,157,0.08)", borderBottom: "1px solid rgba(255,107,157,0.08)", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <h2 className="serif" style={{ fontSize: "clamp(32px,4vw,52px)", fontWeight: 300, textAlign: "center", marginBottom: 56, letterSpacing: "-1px" }}>
              {t.testimonials.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.testimonials.title2}</span>
            </h2>
          </FadeIn>
          <div className="test-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
            {t.testimonials.items.map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="test-card">
                  <div style={{ fontSize: 28, color: "#FF6B9D", marginBottom: 16, opacity: 0.6 }}>"</div>
                  <div className="serif" style={{ fontSize: 18, fontWeight: 300, lineHeight: 1.75, marginBottom: 24, fontStyle: "italic", color: "rgba(240,234,244,0.85)" }}>{item.text}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#FF6B9D,#C44569)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                      {item.name[0]}
                    </div>
                    <div>
                      <div className="sans" style={{ fontSize: 14, fontWeight: 600 }}>{item.name}</div>
                      <div className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.4)" }}>{item.role}</div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" style={{ padding: "100px 24px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 className="serif" style={{ fontSize: "clamp(36px,5vw,60px)", fontWeight: 300, letterSpacing: "-1px", marginBottom: 16 }}>
              {t.pricing.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.pricing.title2}</span>
            </h2>
            <p className="sans" style={{ fontSize: 17, color: "rgba(240,234,244,0.55)", maxWidth: 400, margin: "0 auto" }}>
              {t.pricing.subtitle}
            </p>
          </div>
        </FadeIn>
        <div className="price-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
          {t.pricing.plans.map((plan, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className={`price-card${plan.pop ? " pop" : ""}`} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
                {plan.pop && (
                  <div className="sans" style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg,#FF6B9D,#C44569)", color: "white", padding: "5px 18px", borderRadius: 50, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                    🔥 {t.pricing.mostChosen}
                  </div>
                )}
                <div className="sans" style={{ fontSize: 13, fontWeight: 600, color: "rgba(240,234,244,0.5)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>{plan.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                  <span className="serif" style={{ fontSize: 42, color: "#FF6B9D", fontStyle: "italic" }}>{plan.price}</span>
                  <span className="sans" style={{ fontSize: 14, color: "rgba(240,234,244,0.4)" }}>{plan.period}</span>
                </div>
                <div className="sans" style={{ fontSize: 13, color: "rgba(240,234,244,0.45)", marginBottom: 24 }}>{plan.desc}</div>
                <div style={{ flex: 1, marginBottom: 24 }}>
                  {plan.features.map((f, j) => (
                    <div key={j} className="sans" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", fontSize: 13, color: "rgba(240,234,244,0.65)", borderBottom: j < plan.features.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                      <span style={{ color: "#FF6B9D", fontWeight: 700, fontSize: 14 }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                <button className={plan.ghost ? "btn-ghost" : "btn-primary"} style={{ padding: "13px 20px", fontSize: 14, width: "100%" }}
                  onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}>
                  {plan.cta}
                </button>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.3}>
          <p className="sans" style={{ textAlign: "center", fontSize: 13, color: "rgba(240,234,244,0.3)", marginTop: 28 }}>
            {t.pricing.footer}
          </p>
        </FadeIn>
      </section>

      {/* PRIVACY PROMISE */}
      <section style={{ padding: "80px 24px", background: "rgba(255,107,157,0.04)", borderTop: "1px solid rgba(255,107,157,0.08)", borderBottom: "1px solid rgba(255,107,157,0.08)", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <FadeIn>
            <div style={{ fontSize: 52, marginBottom: 20 }}>🔒</div>
            <h2 className="serif" style={{ fontSize: "clamp(30px,4vw,48px)", fontWeight: 300, letterSpacing: "-1px", marginBottom: 20 }}>
              {t.privacy.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.privacy.title2}</span>
            </h2>
            <p className="sans" style={{ fontSize: 16, color: "rgba(240,234,244,0.55)", lineHeight: 1.8, marginBottom: 36 }}>
              {t.privacy.desc}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
              {t.privacy.badges.map((p, i) => (
                <div key={i} style={{ background: "rgba(255,107,157,0.05)", border: "1px solid rgba(255,107,157,0.15)", borderRadius: 18, padding: "22px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{p.icon}</div>
                  <div className="sans" style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{p.label}</div>
                  <div className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.4)" }}>{p.sub}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: "100px 24px", maxWidth: 720, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <FadeIn>
          <h2 className="serif" style={{ fontSize: "clamp(36px,5vw,56px)", fontWeight: 300, letterSpacing: "-1px", marginBottom: 56, textAlign: "center" }}>
            {t.faq.title1} <span style={{ fontStyle: "italic", color: "#FF6B9D" }}>{t.faq.title2}</span>
          </h2>
        </FadeIn>
        {t.faq.items.map((faq, i) => (
          <FadeIn key={i} delay={i * 0.06}>
            <div className="faq-item" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span className="serif" style={{ fontSize: 20, fontWeight: 400 }}>{faq.q}</span>
                <span style={{ fontSize: 22, color: "#FF6B9D", flexShrink: 0, transition: "transform 0.3s", transform: openFaq === i ? "rotate(45deg)" : "rotate(0)" }}>+</span>
              </div>
              {openFaq === i && (
                <div className="sans" style={{ fontSize: 15, color: "rgba(240,234,244,0.55)", lineHeight: 1.8, marginTop: 16, paddingRight: 32 }}>
                  {faq.a}
                </div>
              )}
            </div>
          </FadeIn>
        ))}
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: "100px 24px 120px", textAlign: "center", position: "relative", zIndex: 1 }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, background: "radial-gradient(circle, rgba(255,107,157,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <FadeIn>
          <div style={{ fontSize: 56, marginBottom: 24, animation: "float 4s ease-in-out infinite" }}>💗</div>
          <h2 className="serif" style={{ fontSize: "clamp(40px,6vw,72px)", fontWeight: 300, letterSpacing: "-2px", marginBottom: 20, lineHeight: 1.1 }}>
            {t.finalCta.title1}<br />
            <span className="glow-text" style={{ fontStyle: "italic" }}>{t.finalCta.title2}</span>
          </h2>
          <p className="sans" style={{ fontSize: 18, color: "rgba(240,234,244,0.5)", marginBottom: 48, maxWidth: 420, margin: "0 auto 48px", lineHeight: 1.7 }}>
            {t.finalCta.subtitle}
          </p>
          {!submitted ? (
            <form onSubmit={handleSubmit} className="hero-email" style={{ display: "flex", gap: 10, maxWidth: 460, margin: "0 auto" }}>
              <input
                className="email-input"
                type="email"
                placeholder={t.hero.placeholder}
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ flex: 1, padding: "16px 24px" }}
                required
              />
              <button type="submit" className="btn-primary" style={{ padding: "16px 30px", fontSize: 15 }}>{t.hero.btn}</button>
            </form>
          ) : (
            <div style={{ display: "inline-block", background: "rgba(255,107,157,0.1)", border: "1px solid rgba(255,107,157,0.3)", borderRadius: 20, padding: "20px 40px" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>💗</div>
              <div className="serif" style={{ fontSize: 22, fontStyle: "italic", color: "#FF6B9D" }}>{t.finalCta.successTitle}</div>
              <div className="sans" style={{ fontSize: 14, color: "rgba(240,234,244,0.5)", marginTop: 4 }}>{t.finalCta.successDesc}</div>
            </div>
          )}
          <p className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.25)", marginTop: 16 }}>
            {count} {t.finalCta.footer}
          </p>
        </FadeIn>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "40px 24px", position: "relative", zIndex: 1 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <div className="serif" style={{ fontSize: 22, fontStyle: "italic", color: "#FF6B9D" }}>Lova</div>
          <div className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.25)", textAlign: "center" }}>
            {t.footer.copy}
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {t.footer.links.map((l, i) => (
              <span key={i} className="sans" style={{ fontSize: 12, color: "rgba(240,234,244,0.3)", cursor: "pointer", transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "#FF6B9D"}
                onMouseLeave={e => e.target.style.color = "rgba(240,234,244,0.3)"}>
                {l}
              </span>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}
